import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  AiOutlineHeart, AiFillHeart, AiOutlineComment, AiOutlineDelete, AiOutlineLeft, AiOutlineRight, AiOutlineEdit
} from "react-icons/ai";
import { PostagemDTO } from "../types";
import { useAuth } from "../../auth/AuthProvider";
import axios from "axios";
import styles from "./styles/PostagemCard.module.css";
import ComentariosModal from "./ComentariosModal";

const BASE_URL = process.env.REACT_APP_API_URL;

interface PostagemCardProps {
  postagem: PostagemDTO;
  onCurtir?: (postagemId: number) => void;
  onExcluir?: (postagemId: number) => void;
  onEditar?: (postagem: PostagemDTO) => void;
}

interface ComentarioDTO {
  id: number;
  texto: string;
  criadoEm: string;
  usuarioId: number;
  usuarioNome: string;
  usuarioFotoUrl?: string;
}

const PostagemCard: React.FC<PostagemCardProps> = ({
  postagem,
  onCurtir,
  onExcluir,
  onEditar,
}) => {
  const navigate = useNavigate();
  const [mostrarMenu, setMostrarMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();
  const [curtida, setCurtida] = useState<boolean>(false);
  const [totalCurtidas, setTotalCurtidas] = useState(postagem.totalCurtidas || 0);
  const [totalComentarios, setTotalComentarios] = useState(postagem.totalComentarios || 0);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [carregandoCurtida, setCarregandoCurtida] = useState(false);
  const [comentariosAleatorios, setComentariosAleatorios] = useState<ComentarioDTO[]>([]);
  const [showComentarios, setShowComentarios] = useState(false);

  const isPostDoUsuarioLogado = user?.id != null && user.id === postagem.usuarioId;
  const arquivoAtual = postagem.arquivos?.[currentFileIndex];
  const urlCompleta = arquivoAtual ? `${BASE_URL}${arquivoAtual.url}` : "";

  // 🔹 Verifica curtida inicial
  useEffect(() => {
    if (!user) return;
    const fetchJaCurtiu = async () => {
      try {
        setCarregandoCurtida(true);
        const token = localStorage.getItem("token");
        const res = await axios.get<boolean>(
          `${BASE_URL}/postagens/${postagem.id}/curtida`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setCurtida(res.data);
      } catch (error) {
        console.error("Erro ao verificar curtida:", error);
      } finally {
        setCarregandoCurtida(false);
      }
    };
    fetchJaCurtiu();
  }, [postagem.id, user]);

  useEffect(() => {
    const fetchComentariosAleatorios = async () => {
      try {
        const res = await axios.get<ComentarioDTO[]>(
          `${BASE_URL}/postagens/${postagem.id}/comentarios/aleatorios?limite=2`
        );
        setComentariosAleatorios(res.data);
      } catch (err) {
        console.error("Erro ao carregar comentários aleatórios:", err);
      }
    };

    fetchComentariosAleatorios();
  }, [postagem.id]);

  // 🔹 Fecha o menu ao clicar fora
  useEffect(() => {
    const handleClickFora = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMostrarMenu(false);
      }
    };

    if (mostrarMenu) {
      document.addEventListener("mousedown", handleClickFora);
    } else {
      document.removeEventListener("mousedown", handleClickFora);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickFora);
    };
  }, [mostrarMenu]);

  // 🔹 Curtir / Descurtir
  const toggleCurtida = async () => {
    if (!user) {
      alert("Você precisa estar logado para curtir postagens.");
      return;
    }
    try {
      setCarregandoCurtida(true);
      const token = localStorage.getItem("token");
      if (curtida) {
        await axios.delete(`${BASE_URL}/postagens/${postagem.id}/curtir`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCurtida(false);
        setTotalCurtidas((prev) => Math.max(prev - 1, 0));
      } else {
        await axios.post(
          `${BASE_URL}/postagens/${postagem.id}/curtir`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setCurtida(true);
        setTotalCurtidas((prev) => prev + 1);
      }
      if (onCurtir) onCurtir(postagem.id);
    } catch (error) {
      console.error("Erro ao curtir/descurtir postagem:", error);
      alert("Não foi possível atualizar a curtida.");
    } finally {
      setCarregandoCurtida(false);
    }
  };

  // 🔹 Excluir postagem
  const handleExcluir = async () => {
    if (!window.confirm("Tem certeza que deseja excluir esta postagem?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${BASE_URL}/postagens/${postagem.id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (onExcluir) onExcluir(postagem.id);
    } catch (error) {
      console.error("Erro ao excluir postagem:", error);
      alert("Não foi possível excluir a postagem.");
    }
  };

  const handleRedirecionarPorPacote = () => {
    if (!postagem.pacoteId) return;
    const queryParams = new URLSearchParams();
    queryParams.set("pacoteId", String(postagem.pacoteId));
    navigate(`/user/feed/explore?${queryParams.toString()}`);
  };

  const handleRedirecionarPorPonto = (ponto: string) => {
    if (!ponto) return;
    const queryParams = new URLSearchParams();
    queryParams.set("pontoNome", ponto);
    navigate(`/user/feed/explore?${queryParams.toString()}`);
  };

  // 🔹 Navegação slider
  const prevFile = () => {
    setCurrentFileIndex((prev) =>
      prev === 0 ? postagem.arquivos!.length - 1 : prev - 1
    );
  };
  const nextFile = () => {
    setCurrentFileIndex((prev) =>
      prev === postagem.arquivos!.length - 1 ? 0 : prev + 1
    );
  };

  return (
    <>
      <div className={styles.card}>
        {/* Cabeçalho */}
        <div className={styles.header}>
          <img
            src={postagem.usuarioFotoUrl ? `${BASE_URL}${postagem.usuarioFotoUrl}` : "/default-avatar.png"}
            alt={postagem.usuarioNome || "Usuário"}
            className={styles.userPhoto}
          />
          <div style={{ flex: 1 }}>
            <p className={styles.userName}>{postagem.usuarioNome || "Usuário"}</p>
            <p className={styles.timestamp}>
              {postagem.criadoEm ? new Date(postagem.criadoEm).toLocaleString() : ""}
            </p>
            {postagem.pacoteTitulo && (
              <div className={styles.pacoteTituloWrapper} ref={menuRef}>
                <p
                  className={styles.pacoteTitulo}
                  onClick={() => setMostrarMenu((prev) => !prev)}
                >
                  📦 {postagem.pacoteTitulo}
                </p>

                {mostrarMenu && (
                  <div className={styles.pacoteMenu}>
                    <button
                      onClick={() => {
                        setMostrarMenu(false);
                        handleRedirecionarPorPacote();
                      }}
                    >
                      Ver postagens do pacote
                    </button>
                    <button
                      onClick={() => {
                        setMostrarMenu(false);
                        navigate(`/flight-details/${postagem.pacoteId}`);
                      }}
                    >
                      Ver página do pacote
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {isPostDoUsuarioLogado && (
            <div style={{ display: "flex", gap: "8px" }}>
              <button
                onClick={handleExcluir}
                className={styles.deleteButton}
                title="Excluir postagem"
              >
                <AiOutlineDelete size={20} />
              </button>
              {onEditar && (
                <button
                  onClick={() => onEditar(postagem)}
                  className={styles.editButton}
                  title="Editar postagem"
                >
                  <AiOutlineEdit />
                </button>
              )}
            </div>
          )}
        </div>

        {/* Conteúdo */}
        <div className={styles.content}>
          {postagem.conteudo && (
            <p className={styles.textContent}>{postagem.conteudo}</p>
          )}

          {arquivoAtual && (
            <div className={styles.sliderContainer}>
              <div className={styles.fileWrapper}>
                {arquivoAtual.tipo?.startsWith("video") ? (
                  <video
                    src={urlCompleta}
                    controls
                    muted
                    loop
                    className={styles.filePhoto}
                  />
                ) : (
                  <img
                    src={urlCompleta}
                    alt={arquivoAtual.pontoInteresse || "Arquivo"}
                    className={styles.filePhoto}
                  />
                )}

                {arquivoAtual.pontoInteresse && (
                  <div
                    className={styles.pontoOverlay}
                    onClick={() => handleRedirecionarPorPonto(arquivoAtual.pontoInteresse!)}
                  >
                    📍 {arquivoAtual.pontoInteresse}
                  </div>
                )}
              </div>

              {postagem.arquivos.length > 1 && (
                <>
                  <button
                    onClick={prevFile}
                    className={`${styles.sliderButton} ${styles.left}`}
                  >
                    <AiOutlineLeft size={30} />
                  </button>
                  <button
                    onClick={nextFile}
                    className={`${styles.sliderButton} ${styles.right}`}
                  >
                    <AiOutlineRight size={30} />
                  </button>
                  <div className={styles.sliderIndicator}>
                    {currentFileIndex + 1} / {postagem.arquivos.length}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Rodapé */}
        <div className={styles.footer}>
          <div className={styles.actions}>
            <button
              onClick={toggleCurtida}
              className={styles.iconButton}
              disabled={carregandoCurtida}
            >
              {curtida ? (
                <AiFillHeart style={{ color: "red", fontSize: 24 }} />
              ) : (
                <AiOutlineHeart style={{ fontSize: 24 }} />
              )}
            </button>
            <button
              onClick={() => setShowComentarios(true)}
              className={styles.iconButton}
            >
              <AiOutlineComment style={{ fontSize: 24 }} />
            </button>
          </div>
          <p className={styles.likes}>{totalCurtidas} curtidas</p>
          {/* Comentários aleatórios */}
          <div className={styles.commentsPreview}>
            {comentariosAleatorios.length > 0 ? (
              comentariosAleatorios.map((c) => (
                <p
                  key={c.id}
                  className={styles.commentPreviewText}
                  onClick={() => setShowComentarios(true)}
                >
                  <strong>{c.usuarioNome}:</strong> {c.texto}
                </p>
              ))
            ) : (
              <p className={styles.comments}>{totalComentarios} comentários</p>
            )}
          </div>
        </div>
      </div>

      {/* Modal de Comentários */}
      {showComentarios && (
        <ComentariosModal
          postagemId={postagem.id}
          show={showComentarios}
          onClose={() => setShowComentarios(false)}
          onChangeTotal={(novoTotal) => setTotalComentarios(novoTotal)} // 🔹 atualiza contagem no card
        />
      )}
    </>
  );
};

export default PostagemCard;